import { ReactElement } from "react";
import { auth } from "../firebase";
import { signInWithRedirect, signInWithPopup, GoogleAuthProvider } from "firebase/auth";
import logo from "../assets/BudgetTrace Logomark_V2.svg";

import {
  Box,
  Button,
  Typography,
  Paper,
  Stack,
  IconButton,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

interface LoginPageProps {
  onBack?: () => void;
  requiredEmail?: string; // Email that must be used for login (for invitations)
  compact?: boolean; // Compact mode for invitation pages
}

function LoginPage({ onBack, requiredEmail, compact = false }: LoginPageProps): ReactElement {
  const handleLogin = async (): Promise<void> => {
    try {
      // Always create a new provider with prompt: 'select_account' to force account chooser
      // This ensures users can always choose a different account even if one is already signed in
      const providerToUse = new GoogleAuthProvider();
      const customParams: { prompt?: string; login_hint?: string } = {
        prompt: 'select_account', // Always show account chooser
      };
      
      // If a specific email is required (e.g., for invitations), set login_hint
      // This helps Google show that email first in the account picker
      if (requiredEmail) {
        customParams.login_hint = requiredEmail;
      }
      
      providerToUse.setCustomParameters(customParams);
      
      console.log("Initiating Google sign-in...", {
        prompt: customParams.prompt,
        login_hint: customParams.login_hint || 'none',
        currentOrigin: window.location.origin,
        hostname: window.location.hostname,
        authDomain: auth.app.options.authDomain
      });
      
      // Try popup first for localhost and local IP addresses (more reliable), fallback to redirect for production
      // Redirect flow can have issues with custom authDomain on localhost/IP
      // Detect local IP addresses (192.168.x.x, 10.x.x.x, 172.16-31.x.x) and treat as localhost
      const hostname = window.location.hostname;
      const isLocalhost = 
        hostname === 'localhost' || 
        hostname === '127.0.0.1';
      
      const isIPAddress = 
        hostname.match(/^192\.168\./) ||
        hostname.match(/^10\./) ||
        hostname.match(/^172\.(1[6-9]|2[0-9]|3[01])\./);
      
      if (isLocalhost || isIPAddress) {
        if (isIPAddress) {
          // Try redirect flow for IP addresses
          // Note: This will likely fail because Firebase checks the origin (IP) against authorized domains
          console.log("Attempting login via IP address:", hostname);
          console.log("Current origin:", window.location.origin);
          console.log("Auth domain:", auth.app.options.authDomain);
          
          try {
            // Try redirect - Firebase will validate origin first
            await signInWithRedirect(auth, providerToUse);
            // If successful, user will be redirected to Google
            return;
          } catch (redirectErr: any) {
            console.error("Login failed for IP address:", redirectErr);
            console.error("Error code:", redirectErr.code);
            console.error("Error message:", redirectErr.message);
            
            // Check if it's an unauthorized-domain error
            if (redirectErr.code === 'auth/unauthorized-domain') {
              alert(
                "⚠️ Login not available via IP address\n\n" +
                "Firebase Authentication doesn't support IP addresses.\n\n" +
                "To test login on iPhone:\n" +
                "• Use https://budgettrace.io (production URL)\n" +
                "• Or test on laptop: http://localhost:3000\n\n" +
                "Note: Hot reload only works with IP address, not production URL."
              );
            } else {
              alert(
                `⚠️ Login failed: ${redirectErr.message || redirectErr.code || 'Unknown error'}\n\n` +
                "To test login:\n" +
                "• Use http://localhost:3000 on your laptop\n" +
                "• Or use https://budgettrace.io on your iPhone"
              );
            }
            return;
          }
        }
        
        // Use popup for actual localhost - more reliable
        // NEVER use redirect for localhost as it causes ERR_CONNECTION_REFUSED
        console.log("Using popup flow for localhost:", hostname);
        console.log("Current URL:", window.location.href);
        console.log("Auth domain:", auth.app.options.authDomain);
        
        try {
          const result = await signInWithPopup(auth, providerToUse);
          console.log("✅ Login successful via popup:", result.user.email);
          // Popup flow completes immediately, no redirect handling needed
        } catch (popupErr: any) {
          console.error("❌ Popup login error:", popupErr);
          console.error("Error code:", popupErr.code);
          console.error("Error message:", popupErr.message);
          
          // NEVER fall back to redirect for localhost - it will fail
          // Show specific error messages for each case
          if (popupErr.code === 'auth/popup-blocked') {
            alert(
              "⚠️ Popup blocked by browser\n\n" +
              "Please allow popups for this site and try again.\n\n" +
              "To allow popups:\n" +
              "1. Look for a popup blocker icon (🚫) in your browser's address bar\n" +
              "2. Click it and select 'Always allow popups from this site'\n" +
              "3. Refresh the page and try logging in again\n\n" +
              "Note: We use popup (not redirect) for localhost to avoid connection errors."
            );
            return;
          } else if (popupErr.code === 'auth/popup-closed-by-user') {
            // User closed popup - just return silently
            console.log("User closed popup");
            return;
          } else if (popupErr.code === 'auth/unauthorized-domain') {
            alert(
              "⚠️ Unauthorized domain\n\n" +
              "Please verify 'localhost' is in authorized domains:\n\n" +
              "1. Go to: https://console.firebase.google.com/project/budget-trace/authentication/settings\n" +
              "2. Scroll to 'Authorized domains'\n" +
              "3. Make sure 'localhost' is in the list\n" +
              "4. If not, click 'Add domain' and add 'localhost'\n" +
              "5. Wait a few seconds, then try again"
            );
            return;
          } else if (popupErr.message?.includes('Cross-Origin-Opener-Policy') || popupErr.message?.includes('COOP')) {
            // COOP policy blocking popup - fall back to redirect
            console.warn("COOP policy blocking popup, falling back to redirect...");
            try {
              await signInWithRedirect(auth, providerToUse);
              // Redirect will happen, user will be redirected to Google then back
              return;
            } catch (redirectErr: any) {
              alert(
                `⚠️ Authentication failed due to browser security policy\n\n` +
                `Error: ${redirectErr.message || 'Unknown error'}\n\n` +
                `Please try:\n` +
                `• Using a different browser\n` +
                `• Disabling strict privacy/security extensions\n` +
                `• Using the production URL: https://budgettrace.io`
              );
              return;
            }
          } else {
            // Other errors - show detailed error
            console.error("Full error object:", popupErr);
            alert(
              `⚠️ Login failed\n\n` +
              `Error: ${popupErr.message || popupErr.code || 'Unknown error'}\n\n` +
              `Please check:\n` +
              `• Browser console (F12) for more details\n` +
              `• Your internet connection\n` +
              `• That popups are allowed\n` +
              `• Firebase Console settings\n\n` +
              `If the error persists, try:\n` +
              `• Hard refresh (Ctrl+Shift+R or Cmd+Shift+R)\n` +
              `• Clear browser cache\n` +
              `• Try a different browser`
            );
            return;
          }
        }
      } else {
        // Use redirect for production (budgettrace.io)
        // NEVER use redirect for localhost - always use popup
        if (hostname === 'localhost' || hostname === '127.0.0.1') {
          console.error("⚠️ ERROR: Attempted to use redirect for localhost! This should never happen.");
          alert(
            "⚠️ Configuration Error\n\n" +
            "The app tried to use redirect authentication for localhost, which will fail.\n\n" +
            "Please:\n" +
            "1. Hard refresh the page (Ctrl+Shift+R or Cmd+Shift+R)\n" +
            "2. Clear browser cache\n" +
            "3. Try again\n\n" +
            "If the problem persists, check the browser console (F12) for errors."
          );
          return;
        }
        
        console.log("Using redirect flow for production...");
        await signInWithRedirect(auth, providerToUse);
        // User will be redirected to Google, then back to the app
        // The redirect result will be handled in App.tsx
      }
    } catch (err: any) {
      // Redirect flow errors are rare, but handle them
      console.error("Login redirect failed:", err);
      // Show error to user (you could add a snackbar/alert here if needed)
      alert(`Login failed: ${err.message || 'Unknown error'}. Please check your browser settings and try again.`);
    }
  };

  const handleEmailLogin = (): void => {
    // TODO: Implement email/password login
  };

  // If compact mode, just return the buttons without the full page wrapper
  if (compact) {
    return (
      <Stack spacing={1.5} sx={{ width: "100%" }}>
        <Button
          fullWidth
          variant="contained"
          onClick={handleLogin}
          sx={{ py: 1.5 }}
          size="large"
        >
          Continue with Google
        </Button>

        <Button
          fullWidth
          variant="outlined"
          onClick={handleEmailLogin}
          disabled
          sx={{ py: 1.5 }}
          size="large"
        >
          Continue with Email
        </Button>
      </Stack>
    );
  }

  return (
    <Box
      minHeight="100vh"
      display="flex"
      alignItems="center"
      justifyContent="center"
      bgcolor="background.default"
      position="relative"
    >
      {/* Back Button */}
      {onBack && (
        <IconButton
          onClick={onBack}
          sx={{
            position: "absolute",
            top: { xs: 16, sm: 24 },
            left: { xs: 16, sm: 24 },
            bgcolor: "background.paper",
            boxShadow: 1,
            "&:hover": {
              bgcolor: "background.paper",
              boxShadow: 2,
            },
          }}
        >
          <ArrowBackIcon />
        </IconButton>
      )}
      <Paper
        elevation={0}
        sx={{
          width: 360,
          p: 4,
          borderRadius: 3,
        }}
      >
        <Stack spacing={4} alignItems="center">
          {/* Logo and Title Group */}
          <Stack spacing={1.5} alignItems="center">
            <img src={logo} alt="Logo" width={120} />
            <Typography
              variant="h4"
              fontWeight="bold"
              noWrap
            >
              Budget-Trace
            </Typography>
          </Stack>

          {/* Login Buttons */}
          <Stack spacing={2} sx={{ width: "100%" }}>
            <Button
              fullWidth
              variant="contained"
              onClick={handleLogin}
              sx={{ py: 1.5 }}
              size="large"
            >
              Continue with Google
            </Button>

            <Button
              fullWidth
              variant="outlined"
              onClick={handleEmailLogin}
              disabled
              sx={{ py: 1.5 }}
              size="large"
            >
              Continue with Email
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
}

export default LoginPage;
